import {
  openCreatePicker,
  useCanCreate,
} from "@components/CreateOptionPickerCst/host";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  Pencil,
  // Trash2,
  Search,
  UserPlus,
  Building2,
  Users,
  ListFilter,
  Copy,
  GraduationCap,
  Folder,
  Folders,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import SortMenuCst from "@components/SortMenuCst";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
import SearchFiltersAccordion from "./search/SearchFiltersAccordion";
import { useLOFilterableFields } from "./search/useLOFilterableFields";
import { useLOSearch } from "./search/useLOSearch";
import BulkActionBarCst from "@components/BulkActionBarCst";
import PaginatorCst from "@components/PaginatorCst";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import {
  buildSearchConditions,
  CONTENT_TYPE_BY_TAB,
  LO_SORT_ORDER_BY,
  SEARCH_FILTER_ORDER_BY_TAB,
} from "./search/types";
import {
  DraftErrorInfo,
  extractPromoteError,
} from "./hooks/parsePromoteErrors";
import { useCatalogPageState } from "./hooks/useCatalogPageState";
import { useDeleteDraft } from "./hooks/useDeleteDraft";
import { usePromoteDraft } from "./hooks/usePromoteDraft";
import { useNormalizedAggregatedItems } from "./hooks/useNormalizedAggregatedItems";
import { CatalogEntityType, DraftListItem, TabKey } from "./types";
import { openEnrollmentModal } from "./Enrollment/host";
import { openAssociateInitiativesModal } from "./AssociateInitiatives/host";
import { openUsersAndGroupsModal } from "./UsersAndGroups/host";
import DraftItem from "./components/DraftItem";
import CatalogItem from "./components/CatalogItem";
import DraftPromoteErrorDialog from "./components/DraftPromoteErrorDialog";
import TabsCst from "@components/TabsCst";
import { InputCst } from "@components/InputCst";
import { ActivityV1Type } from "./AddActivity/types";

const PAGE_SIZE = 20;

const SORT_TO_OS_ORDER_BY = LO_SORT_ORDER_BY;

const CatalogV2: React.FC = () => {
  const { t } = useTranslation("catalog-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "" } = useParams();
  const canCreate = useCanCreate("catalog");
  const { selectedInitiative = "all" } =
    useOutletContext<{ selectedInitiative?: string }>() ?? {};

  const {
    activeTab,
    search,
    fieldFilters,
    sort,
    page,
    setActiveTab,
    update: updateTabFilters,
  } = useCatalogPageState();

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set(),
  );

  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [draftToDelete, setDraftToDelete] = useState<DraftListItem | null>(
    null,
  );
  const [promoteError, setPromoteError] = useState<DraftErrorInfo | null>(null);

  const { deleteDraft, isDeleting } = useDeleteDraft({ corporateId });
  const { promoteDraft, isPromoting } = usePromoteDraft({ corporateId });

  const hasActiveFilters = useMemo(() => {
    const hasValue = (val: any): boolean => {
      if (val === undefined || val === null || val === "") return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") return Object.values(val).some(hasValue);
      return true;
    };
    return Object.values(fieldFilters || {}).some(hasValue);
  }, [fieldFilters]);

  const normalizedSort = sort && SORT_TO_OS_ORDER_BY[sort] ? sort : "id-desc";

  const { fields: filterableFields } = useLOFilterableFields(corporateId);

  const debouncedSearch = useDebouncedValue(search ?? "", 400);

  const osConditions = useMemo(
    () =>
      buildSearchConditions({
        contentTypes: CONTENT_TYPE_BY_TAB[activeTab],
        search: debouncedSearch || undefined,
        fields: filterableFields,
        filterValues: fieldFilters || {},
      }),
    [activeTab, debouncedSearch, filterableFields, fieldFilters],
  );

  const osOrderBy = useMemo(
    () => SORT_TO_OS_ORDER_BY[normalizedSort],
    [normalizedSort],
  );

  const osCatalogueFilters = useMemo(
    () =>
      selectedInitiative !== "all"
        ? { initiative: [Number(selectedInitiative)] }
        : undefined,
    [selectedInitiative],
  );

  const osList = useLOSearch({
    corporateId,
    conditions: osConditions,
    orderBy: osOrderBy,
    page,
    pageSize,
    catalogueFilters: osCatalogueFilters,
  });

  const { items, totalItems, totalPages, isLoading, mutate } = osList;

  const normalizedItems = useNormalizedAggregatedItems(items);

  const segmentForType = (type: CatalogEntityType): string =>
    type === "course" ? "courses" : type === "path" ? "paths" : "activities";

  const displayLoading = isLoading;
  const displayTotalItems = totalItems;
  const displayTotalPages = totalPages;
  const displayEmpty = normalizedItems.length === 0;

  useEffect(() => {
    if (!displayLoading && page > displayTotalPages) {
      updateTabFilters({ page: Math.max(displayTotalPages, 1) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayLoading, page, displayTotalPages]);

  const handleEditDraft = (draft: DraftListItem) => {
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/drafts/${draft.id}/edit`,
    );
  };

  const refreshDraftsAfterRemoval = () => {
    mutate();
  };

  const handlePromoteDraft = async (draft: DraftListItem) => {
    try {
      await promoteDraft(draft.id);
      refreshDraftsAfterRemoval();
    } catch (err) {
      setPromoteError(extractPromoteError(err));
    }
  };

  const confirmDeleteDraft = async () => {
    if (!draftToDelete) return;
    try {
      await deleteDraft(draftToDelete.id);
      setDraftToDelete(null);
      refreshDraftsAfterRemoval();
    } catch (err) {
      console.error("[catalog-v2] delete draft failed", err);
    }
  };

  const handleTabChange = (next: string) => {
    setActiveTab(next as TabKey);
    setSelectedIds(new Set());
  };

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, search, fieldFilters, sort, selectedInitiative]);

  // TODO: selezione multipla fuori wave 1 — per ripristinarla riattivare la
  // logica commentata qui sotto e il checkbox master nella BulkActionBarCst.
  const handleSelectToggle = (entityId: string | number, next: boolean) => {
    setSelectedIds(next ? new Set([entityId]) : new Set());
    // setSelectedIds((prev) => {
    //   const copy = new Set(prev);
    //   if (next) copy.add(entityId);
    //   else copy.delete(entityId);
    //   return copy;
    // });
  };

  // const handleToggleAll = (next: boolean) => {
  //   if (next) {
  //     setSelectedIds(new Set(normalizedItems.map((item) => item.entity.id)));
  //   } else {
  //     setSelectedIds(new Set());
  //   }
  // };

  // const allChecked =
  //   normalizedItems.length > 0 && selectedIds.size === normalizedItems.length;
  // const someChecked = selectedIds.size > 0 && !allChecked;

  const selectedItem = useMemo(
    () => normalizedItems.find((item) => selectedIds.has(item.entity.id)),
    [normalizedItems, selectedIds],
  );

  const handleBulkEnroll = () => {
    if (!selectedItem || !corporateId) return;
    const { entity, entityType: type } = selectedItem;
    openEnrollmentModal({
      objectType: type,
      objectId: entity.id,
      objectTitle: entity.title || entity.name || undefined,
      corporateId,
      isEcm: !!(entity.ecm_specialization || entity.toj_specialization),
      onSuccess: mutate,
    });
  };

  const handleBulkAssociate = () => {
    if (!selectedItem || !corporateId) return;
    const { entity, entityType: type } = selectedItem;
    openAssociateInitiativesModal({
      objectType: type,
      objectId: entity.id,
      objectTitle: entity.title || entity.name || undefined,
      corporateId,
      initialAssociatedIds: Array.isArray(entity.initiative)
        ? entity.initiative
            .map((i: any) => (typeof i === "object" ? i?.id : i))
            .filter((id: any): id is number => typeof id === "number")
        : [],
      onSuccess: mutate,
    });
  };

  const handleBulkUsersGroups = () => {
    if (!selectedItem || !corporateId) return;
    const { entity, entityType: type } = selectedItem;
    openUsersAndGroupsModal({
      initialTab: "enrolled-users",
      objectType: type,
      objectId: entity.id,
      objectTitle: entity.title || entity.name || undefined,
      corporateId,
      isStandalone: entity.is_standalone,
    });
  };

  const handleBulkDuplicate = () => {
    if (!selectedItem || !corporateId) return;
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/${segmentForType(
        selectedItem.entityType,
      )}/${selectedItem.entity.id}/duplicate`,
    );
  };

  const handleBulkEdit = () => {
    if (!selectedItem || !corporateId) return;
    navigate(
      `/v2/corporate/${corporateId}/detail/catalog/${segmentForType(
        selectedItem.entityType,
      )}/${selectedItem.entity.id}/edit`,
    );
  };

  const tabs = [
    {
      key: "all",
      label: t("tabs.all"),
      icon: <></>,
    },
    {
      key: "activities",
      label: t("tabs.activities"),
      icon: <GraduationCap size={"0.75rem"} />,
    },
    {
      key: "courses",
      label: t("tabs.courses"),
      icon: <Folder size={"0.75rem"} />,
    },
    {
      key: "paths",
      label: t("tabs.paths"),
      icon: <Folders size={"0.75rem"} />,
    },
  ];

  const sortOptions = [
    { label: t("sort.id-desc"), value: "id-desc" },
    { label: t("sort.most-recent"), value: "most-recent" },
    { label: t("sort.least-recent"), value: "least-recent" },
    { label: t("sort.alphabetical-az"), value: "alphabetical-az" },
    { label: t("sort.alphabetical-za"), value: "alphabetical-za" },
    { label: t("sort.duration-short"), value: "duration-short" },
    { label: t("sort.duration-long"), value: "duration-long" },
  ];

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <Breadcrumb />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {t("title")}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("description")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Pencil size={16} />}
            onClick={() => openCreatePicker("catalog")}
            disabled={!canCreate}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {t("create-button")}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TabsCst
            tabs={tabs}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </Box>

        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 240,
                "& .MuiFormControl-root": {
                  minWidth: "0 !important",
                  width: "100% !important",
                },
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                "& .MuiFormHelperText-root": { display: "none" },
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
              }}
            >
              <InputCst
                id="catalog-search"
                label={t("search-placeholder") || ""}
                value={search ?? ""}
                onChange={(e: any) =>
                  updateTabFilters({ search: e.target.value, page: 1 })
                }
                endAdornment={
                  <Search size={16} color={theme.palette.text.tertiary} />
                }
              />
            </Box>

            {/* <Box
                sx={{
                  width: 220,
                  "&:empty": { display: "none" },
                  "& .MuiGrid-container": { margin: 0, width: "100%" },
                  "& .MuiGrid-item": {
                    paddingTop: "0 !important",
                    paddingLeft: "0 !important",
                    maxWidth: "100% !important",
                    flexBasis: "100% !important",
                  },
                  "& .MuiFormControl-root": {
                    minWidth: "0 !important",
                    width: "100% !important",
                  },
                }}
              >
                <FiltersBarCst
                  pageId={CATALOG_PAGE_ID_BY_ENTITY[entityType]}
                  value={fieldFilters}
                  onChange={(next) =>
                    updateTabFilters({ fieldFilters: next, page: 1 })
                  }
                  onlyKeys={QUICK_FILTER_KEYS}
                />
              </Box> */}

            <Button
              variant="outlined"
              startIcon={<ListFilter size={16} />}
              onClick={() => setFiltersPanelOpen((prev) => !prev)}
              sx={{
                height: "40px",
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 600,
                flexShrink: 0,
                transition: "none",
                borderColor:
                  hasActiveFilters || filtersPanelOpen
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                color:
                  hasActiveFilters || filtersPanelOpen
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                backgroundColor:
                  hasActiveFilters || filtersPanelOpen
                    ? theme.palette.action.hover
                    : "transparent",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {t("filters-button")}
            </Button>

            <SortMenuCst
              id="catalog-sort"
              value={normalizedSort}
              options={sortOptions}
              onChange={(next) => updateTabFilters({ sort: next || null })}
            />
          </Box>

          <SearchFiltersAccordion
            open={filtersPanelOpen}
            onClose={() => setFiltersPanelOpen(false)}
            fields={filterableFields}
            fieldOrder={SEARCH_FILTER_ORDER_BY_TAB[activeTab]}
            value={fieldFilters || {}}
            onApply={(next) =>
              updateTabFilters({ fieldFilters: next, page: 1 })
            }
          />

          <Box sx={{ mb: 2, mt: 2 }}>
            <BulkActionBarCst
              selectedCount={selectedIds.size}
              // checked={allChecked}
              // indeterminate={someChecked}
              // onToggleAll={handleToggleAll}
              selectedLabel={(count) => t("bulk.selected", { count })}
              actions={[
                {
                  key: "enroll",
                  label: t("bulk.enroll"),
                  icon: <UserPlus size={14} />,
                  onClick: handleBulkEnroll,
                },
                {
                  key: "associate",
                  label: t("bulk.associate-initiative"),
                  icon: <Building2 size={14} />,
                  onClick: handleBulkAssociate,
                },
                {
                  key: "users",
                  label: t("bulk.users-groups"),
                  icon: <Users size={14} />,
                  onClick: handleBulkUsersGroups,
                  disabled:
                    selectedIds.size !== 1 ||
                    selectedItem?.entity?.is_standalone === false,
                },
              ]}
              iconActions={[
                {
                  key: "duplicate",
                  icon: <Copy size={18} />,
                  onClick: handleBulkDuplicate,
                  tooltip: t("bulk.duplicate") || "",
                },
                {
                  key: "edit",
                  icon: <Pencil size={18} />,
                  onClick: handleBulkEdit,
                  tooltip: t("bulk.edit") || "",
                  disabled: selectedIds.size !== 1,
                },
                // TODO: elimina LO fuori da wave 1
                // {
                //   key: "delete",
                //   icon: <Trash2 size={18} />,
                //   onClick: () => {},
                //   tooltip: t("bulk.delete") || "",
                // },
              ]}
            />
          </Box>
        </>

        {displayLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : displayEmpty ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.primary,
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {normalizedItems.map(
              ({ entity, entityType: itemType, isDraft, activityType }) => {
                if (isDraft) {
                  const draft = entity as DraftListItem;
                  return (
                    <DraftItem
                      key={`draft-${draft.id}`}
                      draft={draft}
                      badgeLabel={t("badges.draft")}
                      editLabel={t("draft-actions.edit")}
                      promoteLabel={t("draft-actions.promote")}
                      deleteLabel={t("draft-actions.delete")}
                      onEdit={handleEditDraft}
                      onPromote={handlePromoteDraft}
                      onDelete={setDraftToDelete}
                      actionsDisabled={isPromoting || isDeleting}
                    />
                  );
                }
                const segment = segmentForType(itemType);
                return (
                  <CatalogItem
                    key={`${itemType}-${entity.id}`}
                    entity={entity}
                    entityType={itemType}
                    activityType={activityType as ActivityV1Type}
                    selected={selectedIds.has(entity.id)}
                    onSelect={(next) => handleSelectToggle(entity.id, next)}
                    onEnrolled={mutate}
                    onDetails={(e) =>
                      navigate(
                        `/v2/corporate/${corporateId}/detail/catalog/${segment}/${e.id}`,
                      )
                    }
                    onPublish={(e) => {
                      // TODO: inserire api corretta
                      console.log("publish", itemType, e.id);
                    }}
                    onEdit={(e) =>
                      navigate(
                        `/v2/corporate/${corporateId}/detail/catalog/${segment}/${e.id}/edit`,
                      )
                    }
                  />
                );
              },
            )}
          </Box>
        )}

        {displayTotalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={displayTotalItems}
              onPageChange={(next) => updateTabFilters({ page: next })}
              onPageSizeChange={(next) => {
                setPageSize(next);
                updateTabFilters({ page: 1 });
              }}
            />
          </Box>
        )}

        <ConfirmDialogCst
          open={!!draftToDelete}
          title={t("draft-delete.title")}
          description={t("draft-delete.description")}
          confirmLabel={t("draft-actions.delete")}
          cancelLabel={t("draft-delete.cancel")}
          confirmVariant="danger"
          isSubmitting={isDeleting}
          onClose={() => setDraftToDelete(null)}
          onConfirm={confirmDeleteDraft}
        />

        <DraftPromoteErrorDialog
          error={promoteError}
          onClose={() => setPromoteError(null)}
        />
      </Box>
    </AnimationFadeIn>
  );
};

export default CatalogV2;
